// src/components/providerAssignments/EndAssignmentDialog.tsx
//
// Confirmation flow for ending an assignment (spec §10). Shows the
// current assignment summary, requires end-date + reason, does NOT
// hard-delete anything, and can choose 'completed' (normal end) or
// 'cancelled' (mistake / never happened).

import { useEffect, useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { useHasCapability } from '../../services/centralContext';
import { useEndProviderAssignmentMutation } from '../../api/providerAssignmentsApi';
import type { PersonProviderAssignment } from '../../types/api';
import {
  PRIORITY_LABELS,
  contactLabel,
  providerLabel,
} from './assignmentDisplay';

export interface EndAssignmentDialogProps {
  assignment: PersonProviderAssignment;
  onClose: () => void;
  onEnded?: (row: PersonProviderAssignment) => void;
}

export default function EndAssignmentDialog({
  assignment,
  onClose,
  onEnded,
}: EndAssignmentDialogProps) {
  const canEnd = useHasCapability('assignments', 'end');
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [finalStatus, setFinalStatus] = useState<'completed' | 'cancelled'>('completed');
  const [error, setError] = useState<string | null>(null);
  const [endAssignment, { isLoading }] = useEndProviderAssignmentMutation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    setError(null);
    try {
      const row = await endAssignment({
        id: assignment.id,
        data: {
          endDate: endDate || null,
          reason: reason.trim() || null,
          status: finalStatus,
        },
      }).unwrap();
      onEnded?.(row);
      onClose();
    } catch (err) {
      const e = err as { data?: { error?: string; code?: string } };
      setError(e?.data?.error || 'Failed to end the assignment.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="end-assignment-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isLoading) onClose();
      }}
      data-testid="end-assignment-dialog"
    >
      <div className="bg-white rounded-xl shadow-xl ring-1 ring-slate-200 w-full max-w-md flex flex-col">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
          <h2 id="end-assignment-title" className="flex-1 text-sm font-semibold text-slate-900">
            End assignment
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {!canEnd ? (
            <p className="text-sm text-slate-600">
              You don&apos;t have permission to end provider assignments.
            </p>
          ) : (
            <>
              <div className="rounded-md bg-slate-50 p-3 text-xs">
                <div className="text-slate-500">Assignment</div>
                <div className="mt-0.5 font-medium text-slate-900">
                  {providerLabel(assignment.provider)} → {contactLabel(assignment.contact)}
                </div>
                <div className="mt-1 text-slate-500">
                  {assignment.departmentCircle?.name} · {PRIORITY_LABELS[assignment.priority]}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-slate-700 mb-1">
                    End date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-700 mb-1">
                    Final status
                  </label>
                  <select
                    value={finalStatus}
                    onChange={(e) => setFinalStatus(e.target.value as 'completed' | 'cancelled')}
                    className="block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                  >
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-700 mb-1">
                  Reason
                </label>
                <textarea
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="block w-full resize-none rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                  placeholder="What led to ending the assignment?"
                />
              </div>

              <p className="text-[11px] text-slate-500">
                History is preserved. The assignment is not deleted.
              </p>

              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800" role="alert">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{error}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          {canEnd && (
            <button
              type="button"
              onClick={submit}
              disabled={isLoading}
              className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
              data-testid="end-assignment-submit"
            >
              {isLoading ? 'Ending…' : finalStatus === 'cancelled' ? 'Cancel assignment' : 'End assignment'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
